import type { DynamicGridItem } from '../types';

/** Meta used to build dashboard items (no x/y yet). */
export type DynamicGridItemMeta = Pick<
  DynamicGridItem,
  'id' | 'title' | 'description' | 'w' | 'h' | 'minW' | 'minH'
>;

/** Explicit grid placement for a curated layout. */
export type DynamicGridPosition = Pick<DynamicGridItem, 'x' | 'y' | 'w' | 'h'>;

/**
 * Merges item meta with curated positions. Items missing from `positions`
 * stack flush below the lowest placed card (x: 0), in `orderedMeta` order.
 */
export const buildDashboardItems = <TMeta extends DynamicGridItemMeta>(
  orderedMeta: TMeta[],
  positions: Partial<Record<TMeta['id'], DynamicGridPosition>>
): Array<TMeta & DynamicGridPosition> => {
  const placedPositions = Object.values(positions).filter(
    (pos): pos is DynamicGridPosition => pos != null
  );
  const placedBottom = placedPositions.reduce(
    (max, pos) => Math.max(max, pos.y + pos.h),
    0
  );
  let overflowY = placedBottom;

  return orderedMeta.map((meta) => {
    const position = positions[meta.id as TMeta['id']];
    if (position) {
      return { ...meta, ...position };
    }
    const item: TMeta & DynamicGridPosition = {
      ...meta,
      x: 0,
      y: overflowY,
      w: meta.w,
      h: meta.h,
    };
    overflowY += meta.h;
    return item;
  });
};
